'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu'
import { useInstanceContext, UserInstance } from '@/lib/context/InstanceProvider'
import { useToast } from '@/hooks/use-toast'
import { Plus, CheckCircle, Clock, Users, ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useNavTheme } from '@/lib/context/NavThemeContext'
import { getTeamCreationResumePath } from '@/lib/utils/teamCreationRouting'
import {
  GAME_MODE_CONFIG,
  FALLBACK_DISPLAY_CONFIG,
  getInstanceDisplayConfig
} from '@/lib/instances/instance-display-config'
import { getInstanceCompetitionStatus } from '@/lib/instances/competition-status'

// Re-exported from shared module so existing imports of these symbols from
// this file keep working. New consumers should import directly from
// '@/lib/instances/instance-display-config'.
export { GAME_MODE_CONFIG, FALLBACK_DISPLAY_CONFIG, getInstanceDisplayConfig }

const SCROLL_LOCK_KEYS = new Set(['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', ' '])

function useVisibleScrollbarScrollLock(
  isLocked: boolean,
  allowedScrollContainerRef: React.RefObject<HTMLElement | null>
) {
  const scrollPositionRef = React.useRef({ x: 0, y: 0 })

  React.useEffect(() => {
    if (!isLocked) {
      return
    }

    scrollPositionRef.current = { x: window.scrollX, y: window.scrollY }

    const preventScroll = (event: Event) => {
      if (
        event.target instanceof Node &&
        allowedScrollContainerRef.current?.contains(event.target)
      ) {
        return
      }

      event.preventDefault()
    }

    const preventKeyboardScroll = (event: KeyboardEvent) => {
      if (
        document.activeElement instanceof Node &&
        allowedScrollContainerRef.current?.contains(document.activeElement)
      ) {
        return
      }

      if (SCROLL_LOCK_KEYS.has(event.key)) {
        event.preventDefault()
      }
    }

    const restoreScrollPosition = () => {
      window.scrollTo(scrollPositionRef.current.x, scrollPositionRef.current.y)
    }

    window.addEventListener('wheel', preventScroll, { passive: false })
    window.addEventListener('touchmove', preventScroll, { passive: false })
    window.addEventListener('keydown', preventKeyboardScroll)
    window.addEventListener('scroll', restoreScrollPosition)

    return () => {
      window.removeEventListener('wheel', preventScroll)
      window.removeEventListener('touchmove', preventScroll)
      window.removeEventListener('keydown', preventKeyboardScroll)
      window.removeEventListener('scroll', restoreScrollPosition)
    }
  }, [allowedScrollContainerRef, isLocked])
}

interface InstanceSelectorDropdownProps {
  variant?: 'default' | 'mobileHeader'
}

export default function InstanceSelectorDropdown({
  variant = 'default'
}: InstanceSelectorDropdownProps) {
  const { currentInstance, allInstances, setCurrentInstanceId, isLoading } = useInstanceContext()
  const router = useRouter()
  const { toast } = useToast()
  const { useNewUI } = useNavTheme()
  const [isOpen, setIsOpen] = React.useState(false)
  const [mounted, setMounted] = React.useState(false)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const isMobileHeader = variant === 'mobileHeader'

  useVisibleScrollbarScrollLock(useNewUI && isOpen, contentRef)

  React.useEffect(() => {
    setMounted(true)
  }, [])

  // Handle instance selection with validation
  const handleInstanceSelect = (instance: UserInstance) => {
    setIsOpen(false)
    const isComplete = instance.is_complete === true

    if (!isComplete) {
      toast({
        title: 'Draft Team',
        description: `Resuming "${instance.team_name}" from where you left off.`
      })
      router.push(
        getTeamCreationResumePath({
          instanceId: instance.id,
          creationCompleted: instance.creation_completed,
          creationStep: instance.creation_step
        })
      )
      return
    }

    setCurrentInstanceId(instance.id)
    router.push('/dashboard')
  }

  if (isLoading) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 bg-neu-surface-recessed px-3 py-1 rounded-lg text-sm text-neu-text-secondary',
          isMobileHeader && 'h-8 max-w-[96px] px-2 text-xs'
        )}
      >
        <div
          className={cn(
            'w-6 h-6 bg-neu-surface-dark rounded-full animate-pulse',
            isMobileHeader && 'hidden'
          )}
        />
        <span className="animate-pulse truncate">Loading...</span>
      </div>
    )
  }

  if (!currentInstance && allInstances.length === 0) {
    return (
      <Link
        href="/create/team-name"
        className="flex items-center gap-2 bg-neu-brand-from px-3 py-1 rounded-lg text-sm text-neu-text-primary hover:bg-neu-brand-from/90"
      >
        <Plus className="w-4 h-4" />
        <span>Create Team</span>
      </Link>
    )
  }

  const currentTierConfig = currentInstance ? getInstanceDisplayConfig(currentInstance) : null

  if (isMobileHeader) {
    const triggerLabel = currentInstance?.team_name || currentTierConfig?.label || 'Team'

    const mobileSheet =
      mounted && typeof document !== 'undefined'
        ? createPortal(
            <>
              <div
                className={cn(
                  'fixed inset-0 z-[100] bg-transparent transition-opacity duration-300',
                  isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
                )}
                onClick={() => setIsOpen(false)}
              />

              <aside
                className={cn(
                  'fixed inset-x-0 bottom-0 z-[120] mx-auto w-full max-w-[720px] overflow-hidden rounded-t-2xl border border-white/10 bg-neu-surface shadow-[0_-20px_60px_rgba(0,0,0,0.55)] transition-transform duration-300 ease-out',
                  isOpen ? 'translate-y-0' : 'translate-y-full'
                )}
                aria-hidden={!isOpen}
              >
                <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                  <h2 className="text-[13px] font-bold uppercase tracking-[0.15px] text-neu-text-primary">
                    Your Teams
                  </h2>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-neu-text-secondary transition-colors hover:bg-white/10 hover:text-neu-text-primary"
                    aria-label="Close team selector"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div
                  ref={contentRef}
                  className="max-h-[72dvh] overflow-y-auto px-3 py-4 themed-scrollbar"
                >
                  <div className="flex flex-col gap-2">
                    {allInstances.map((instance) => {
                      const tierConfig = getInstanceDisplayConfig(instance)
                      const isSelected = currentInstance?.id === instance.id
                      const competitionStatus = getInstanceCompetitionStatus(instance)

                      return (
                        <button
                          key={instance.id}
                          type="button"
                          onClick={() => handleInstanceSelect(instance)}
                          className={cn(
                            'flex w-full items-center justify-between rounded-xl px-3 py-3 text-left transition-colors',
                            isSelected
                              ? 'border border-transparent bg-gradient-to-r from-neu-brand-from to-neu-brand-to text-neu-text-on-brand shadow-neumorphic'
                              : 'border border-transparent bg-white/[0.03] hover:bg-white/[0.06]'
                          )}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <Image
                              src={tierConfig.imagePath}
                              alt={`${tierConfig.label} Tier`}
                              width={34}
                              height={34}
                              className="h-9 w-9 flex-none object-contain"
                            />
                            <span
                              className={cn(
                                'truncate font-montserrat text-[13px] font-bold',
                                isSelected ? 'text-neu-text-on-brand' : 'text-neu-text-primary'
                              )}
                            >
                              {instance.team_name || `${tierConfig.label} Team`}
                            </span>
                          </div>

                          <div
                            className="ml-3 flex flex-none items-center"
                            title={competitionStatus.label}
                          >
                            {competitionStatus.tone === 'draft' && (
                              <Clock
                                className={cn(
                                  'h-4 w-4',
                                  isSelected ? 'text-neu-text-on-brand' : 'text-orange-400'
                                )}
                              />
                            )}
                            {competitionStatus.tone === 'ready' && (
                              <CheckCircle
                                className={cn(
                                  'h-4 w-4',
                                  isSelected ? 'text-neu-text-on-brand' : 'text-yellow-400'
                                )}
                              />
                            )}
                            {['joined', 'queued', 'assigned'].includes(competitionStatus.tone) && (
                              <Users
                                className={cn(
                                  'h-4 w-4',
                                  isSelected ? 'text-neu-text-on-brand' : 'text-green-400'
                                )}
                              />
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>

                  <div className="my-3 h-px bg-white/10" />

                  <Link
                    href="/create/team-name"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 rounded-xl px-3 py-3 text-neu-text-primary transition-colors hover:bg-white/[0.06]"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neu-brand-from/20">
                      <Plus className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-[13px] font-medium">Create New Team</div>
                      <div className="text-[11px] text-neu-text-muted">Start a new fantasy team</div>
                    </div>
                  </Link>

                  <Link
                    href="/drafts"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 rounded-xl px-3 py-3 text-neu-text-primary transition-colors hover:bg-white/[0.06]"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neu-surface-recessed">
                      <Users className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-[13px] font-medium">Manage Teams</div>
                      <div className="text-[11px] text-neu-text-muted">View all teams and drafts</div>
                    </div>
                  </Link>
                </div>
              </aside>
            </>,
            document.body
          )
        : null

    return (
      <>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="flex h-8 max-w-[104px] flex-row items-center gap-1.5 rounded-lg bg-white/5 px-2 text-neu-text-primary outline-none transition-opacity hover:opacity-80"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          <span className="min-w-0 max-w-[68px] truncate text-center font-montserrat text-[13px] font-bold uppercase leading-none tracking-[0.15px]">
            {triggerLabel}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 flex-none text-neu-text-primary transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>
        {mobileSheet}
      </>
    )
  }

  return (
    <DropdownMenu modal={!useNewUI} open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button className="group flex min-w-0 flex-row items-center gap-1.5 outline-none transition-opacity hover:opacity-80 lg:gap-2">
          {currentInstance && currentTierConfig ? (
            <>
              <div
                className={cn(
                  'w-[44px] h-[38px] relative flex-shrink-0',
                  useNewUI ? 'hidden' : 'block'
                )}
              >
                <Image
                  src={currentTierConfig.imagePath}
                  alt={`${currentTierConfig.label} Tier`}
                  fill
                  sizes="44px"
                  className="object-contain"
                />
              </div>
              <span className="min-w-0 max-w-[72px] truncate text-center font-montserrat text-sm font-bold uppercase leading-[20px] tracking-[0.15px] text-neu-text-primary sm:max-w-[96px] lg:max-w-[120px] lg:text-[14px] lg:leading-[20px] xl:max-w-[200px] xl:text-[16px] xl:leading-[24px]">
                {currentInstance.team_name || currentTierConfig.label}
              </span>
              <ChevronDown className="h-4 w-4 flex-none text-neu-text-primary transition-transform group-data-[state=open]:rotate-180 lg:h-[18px] lg:w-[18px]" />
            </>
          ) : (
            <>
              <div className="w-[44px] h-[38px] bg-neu-surface-recessed rounded flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-neu-text-primary" />
              </div>
              <span className="font-montserrat text-sm font-bold uppercase leading-[20px] tracking-[0.15px] text-neu-text-primary lg:text-[16px] lg:leading-[24px]">
                Select Team
              </span>
              <ChevronDown className="h-4 w-4 flex-none text-neu-text-primary lg:h-[18px] lg:w-[18px]" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        ref={contentRef}
        align="end"
        className="w-80 bg-neu-surface/95 backdrop-blur-md border-neu-border-soft"
      >
        <div className="p-2">
          <div className="text-[13px] font-medium text-neu-text-muted mb-2 px-2">Your Teams</div>

          {allInstances.map((instance) => {
            const tierConfig = getInstanceDisplayConfig(instance)
            const isSelected = currentInstance?.id === instance.id
            const competitionStatus = getInstanceCompetitionStatus(instance)

            return (
              <DropdownMenuItem
                key={instance.id}
                onClick={() => handleInstanceSelect(instance)}
                className={cn(
                  'p-3 rounded-md transition-all',
                  isSelected
                    ? 'bg-neu-brand-from/20 border-neu-brand-from/50 border'
                    : 'hover:bg-neu-surface-recessed',
                  'cursor-pointer'
                )}
              >
                <div className="flex items-center gap-3 w-full justify-between">
                  {/* Left: Icon + Name */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Image
                      src={tierConfig.imagePath}
                      alt={`${tierConfig.label} Tier`}
                      width={32}
                      height={32}
                      className="w-8 h-8 object-contain flex-shrink-0"
                    />
                    <span className="font-medium text-neu-text-primary truncate">
                      {instance.team_name || `${tierConfig.label} Team`}
                    </span>
                  </div>

                  {/* Right: Status Indicator */}
                  <div className="flex items-center gap-2" title={competitionStatus.label}>
                    {competitionStatus.tone === 'draft' && (
                      <Clock className="w-4 h-4 text-orange-400 flex-shrink-0" />
                    )}
                    {competitionStatus.tone === 'ready' && (
                      <CheckCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                    )}
                    {['joined', 'queued', 'assigned'].includes(competitionStatus.tone) && (
                      <Users className="w-4 h-4 text-green-400 flex-shrink-0" />
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
            )
          })}

          <DropdownMenuSeparator className="my-2 bg-neu-border-soft" />

          {/* Create New Team */}
          <DropdownMenuItem asChild>
            <Link
              href="/create/team-name"
              className="flex items-center gap-3 p-3 cursor-pointer rounded-md hover:bg-neu-surface-recessed text-neu-text-primary"
            >
              <div className="w-8 h-8 bg-neu-brand-from/20 rounded-full flex items-center justify-center">
                <Plus className="w-4 h-4" />
              </div>
              <div>
                <div className="font-medium">Create New Team</div>
                <div className="text-[13px] text-neu-text-muted">Start a new fantasy team</div>
              </div>
            </Link>
          </DropdownMenuItem>

          {/* View All Teams */}
          <DropdownMenuItem asChild>
            <Link
              href="/drafts"
              className="flex items-center gap-3 p-3 cursor-pointer rounded-md hover:bg-neu-surface-recessed"
            >
              <div className="w-8 h-8 bg-neu-surface-recessed rounded-full flex items-center justify-center">
                <Users className="w-4 h-4 text-neu-text-primary" />
              </div>
              <div>
                <div className="font-medium text-neu-text-primary">Manage Teams</div>
                <div className="text-[13px] text-neu-text-muted">View all your teams and drafts</div>
              </div>
            </Link>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
